import { useId, useState } from 'react'

import type { FeedItem, ModuleNode } from '../api/types'
import { t } from '../i18n'
import { ErrorNotice, Pending, formatDate } from '../ui'
import { useFeed } from './FeedCard'

/** Days the chart covers, today included. */
export const CHART_DAYS = 14

/** Modules drawn as their own line; the rest are summed as "Other". */
const LINES = 3

/** Validated together (light and dark) for four adjacent series. */
const SERIES = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)']

/**
 * One line. `counts[i]` is the rows counted on day `i`, or `null` where the
 * feed page did not cover the whole day -- a day it did not reach is not a
 * quiet day, and is not drawn or totalled as one.
 */
export type Series = { key: string; counts: (number | null)[]; total: number }
export type Chart = {
  days: string[]
  /** Whether each day was covered in full by the page that was read. */
  known: boolean[]
  series: Series[]
  /** The first day covered in full, when the page stops short. */
  from: string
  partial: boolean
  /** Whether "Other" adds several modules together. */
  grouped: boolean
}

/** A local calendar day, `2026-10-21`. */
function dayOf(moment: Date): string {
  return moment.toLocaleDateString('en-CA')
}

/**
 * Count the feed's rows per day per module over the last fourteen days.
 *
 * The feed answers its newest page, not a history. When that page is full it
 * may stop part-way through a day, and every day before that one it did not
 * reach at all. Those days are unknown rather than zero: their counts are
 * `null`, they are left out of the lines and the totals, and the card says
 * from which day the counts are whole. A page that is not full is the whole
 * history, so every day in it is known, empty days included.
 */
export function chartOf(items: FeedItem[], today: Date, full: boolean): Chart {
  const days: string[] = []
  for (let back = CHART_DAYS - 1; back >= 0; back -= 1) {
    const day = new Date(today)
    day.setDate(day.getDate() - back)
    days.push(dayOf(day))
  }
  const first = days[0] as string
  const oldest = items.reduce<string | null>((low, item) => {
    const day = dayOf(new Date(item.occurred_at))
    return low === null || day < low ? day : low
  }, null)
  // The oldest day on a full page may have been cut part-way through, so it
  // is not whole either; only the days after it are.
  const partial = full && oldest !== null && oldest >= first
  const known = days.map((day) => !partial || (oldest !== null && day > oldest))
  const from = partial ? (days.find((_, index) => known[index]) ?? '') : first

  const byModule = new Map<string, number[]>()
  for (const item of items) {
    const at = days.indexOf(dayOf(new Date(item.occurred_at)))
    if (at < 0) continue
    const counts = byModule.get(item.module) ?? days.map(() => 0)
    counts[at] = (counts[at] ?? 0) + 1
    byModule.set(item.module, counts)
  }
  const measured = (counts: number[]): (number | null)[] =>
    counts.map((count, index) => (known[index] ? count : null))
  const sum = (counts: (number | null)[]) => counts.reduce<number>((a, b) => a + (b ?? 0), 0)
  const ranked = [...byModule.entries()]
    .map(([key, counts]) => {
      const kept = measured(counts)
      return { key, counts: kept, total: sum(kept) }
    })
    .filter((each) => each.total > 0 || !partial)
    .sort((a, b) => b.total - a.total || a.key.localeCompare(b.key))
  const series: Series[] = ranked.slice(0, LINES)
  const rest = ranked.slice(LINES)
  if (rest.length > 0) {
    const counts = days.map((_, index) =>
      known[index] ? rest.reduce((total, each) => total + (each.counts[index] ?? 0), 0) : null,
    )
    series.push({ key: '', counts, total: sum(counts) })
  }
  return { days, known, series, from, partial, grouped: rest.length > 1 }
}

/** The runs of known days, as point lists: a gap in coverage is a gap in the line. */
function runs(counts: (number | null)[]): { day: number; count: number }[][] {
  const out: { day: number; count: number }[][] = []
  let current: { day: number; count: number }[] = []
  counts.forEach((count, day) => {
    if (count === null) {
      if (current.length > 0) out.push(current)
      current = []
    } else current.push({ day, count })
  })
  if (current.length > 0) out.push(current)
  return out
}

const WIDTH = 560
const HEIGHT = 160
const PAD = { left: 28, right: 8, top: 8, bottom: 20 }

/**
 * Activity per day on this project, one line per module, from the same feed
 * the Activity card shows.
 */
export function ActivityChart({
  projectId,
  modules = [],
}: {
  projectId: string
  modules?: readonly ModuleNode[]
}) {
  const titleId = useId()
  const { query: feed, data } = useFeed(projectId)
  const [hover, setHover] = useState<number | null>(null)

  const nameOf = (key: string): string =>
    key === ''
      ? t('home.chart.other')
      : (modules.find((module) => module.route_prefix === `/${key}` || module.tool_code === key)
          ?.name ?? key)

  const items = data?.items ?? []
  // A page of 200 that is full may have stopped short of fourteen days.
  const chart = chartOf(items, new Date(), items.length >= 200)
  const peak = Math.max(1, ...chart.series.flatMap((each) => each.counts.map((n) => n ?? 0)))
  const x = (index: number) =>
    PAD.left + (index * (WIDTH - PAD.left - PAD.right)) / (chart.days.length - 1)
  const y = (count: number) =>
    HEIGHT - PAD.bottom - (count / peak) * (HEIGHT - PAD.top - PAD.bottom)
  const step = (WIDTH - PAD.left - PAD.right) / (chart.days.length - 1)

  return (
    <section className="card activity-chart" aria-labelledby={titleId}>
      <h3 id={titleId}>{t('home.chart.title')}</h3>
      {feed.isPending ? <Pending /> : null}
      {feed.error ? <ErrorNotice error={feed.error} retry={() => void feed.refetch()} /> : null}
      {/* Nothing to draw says "no activity" only when every day was read. A
          read that covered part of the window, or no whole day, says what it
          covered instead: an unread day is not a quiet one. */}
      {data && chart.series.length === 0 ? (
        <p className="muted">
          {!chart.partial
            ? t('home.chart.empty')
            : chart.from
              ? t('home.chart.empty_partial', { from: formatDate(chart.from) })
              : t('home.chart.partial_none')}
        </p>
      ) : null}
      {chart.series.length > 0 ? (
        <>
          <ul className="chart-legend">
            {chart.series.map((each, index) => (
              <li key={each.key || 'other'}>
                <span className="swatch" style={{ background: SERIES[index] }} aria-hidden="true" />
                {nameOf(each.key)} <span className="muted">{each.total}</span>
              </li>
            ))}
          </ul>
          <div className="chart-plot">
            <svg
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              role="img"
              aria-label={t('home.chart.title')}
              onMouseLeave={() => setHover(null)}
            >
              <line className="axis" x1={PAD.left} x2={WIDTH - PAD.right} y1={y(0)} y2={y(0)} />
              <line
                className="grid"
                x1={PAD.left}
                x2={WIDTH - PAD.right}
                y1={y(peak)}
                y2={y(peak)}
              />
              <text className="tick" x={PAD.left - 4} y={y(peak) + 4} textAnchor="end">
                {peak}
              </text>
              <text className="tick" x={PAD.left - 4} y={y(0) + 4} textAnchor="end">
                0
              </text>
              {[0, chart.days.length - 1].map((index) => (
                <text
                  key={index}
                  className="tick"
                  x={x(index)}
                  y={HEIGHT - 4}
                  textAnchor={index === 0 ? 'start' : 'end'}
                >
                  {formatDate(chart.days[index])}
                </text>
              ))}
              {hover !== null ? (
                <line className="crosshair" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={y(0)} />
              ) : null}
              {/* The days the page did not cover in full, shaded and undrawn. */}
              {chart.days.map((day, index) =>
                chart.known[index] ? null : (
                  <rect
                    key={`unknown-${day}`}
                    className="unknown"
                    x={x(index) - step / 2}
                    y={PAD.top}
                    width={step}
                    height={y(0) - PAD.top}
                  />
                ),
              )}
              {chart.series.flatMap((each, index) =>
                runs(each.counts).map((run) =>
                  run.length === 1 ? (
                    <circle
                      key={`${each.key || 'other'}-${run[0]?.day}`}
                      cx={x(run[0]?.day ?? 0)}
                      cy={y(run[0]?.count ?? 0)}
                      r={3}
                      fill={SERIES[index]}
                    />
                  ) : (
                    <polyline
                      key={`${each.key || 'other'}-${run[0]?.day}`}
                      fill="none"
                      stroke={SERIES[index]}
                      strokeWidth={2}
                      strokeLinejoin="round"
                      points={run.map((point) => `${x(point.day)},${y(point.count)}`).join(' ')}
                    />
                  ),
                ),
              )}
              {chart.days.map((day, index) => (
                <rect
                  key={day}
                  className="hit"
                  x={x(index) - step / 2}
                  y={0}
                  width={step}
                  height={HEIGHT}
                  onMouseEnter={() => setHover(index)}
                />
              ))}
            </svg>
            {hover !== null ? (
              <div
                className="chart-tip"
                style={{ left: `${(x(hover) / WIDTH) * 100}%` }}
                role="status"
              >
                <strong>{formatDate(chart.days[hover])}</strong>
                {chart.series.map((each, index) => (
                  <div key={each.key || 'other'}>
                    <span
                      className="swatch"
                      style={{ background: SERIES[index] }}
                      aria-hidden="true"
                    />
                    {nameOf(each.key)} {each.counts[hover] ?? t('home.chart.not_read')}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          {chart.grouped ? <p className="muted">{t('home.chart.grouped')}</p> : null}
          {chart.partial ? (
            <p className="muted">
              {chart.from
                ? t('home.chart.partial', { from: formatDate(chart.from) })
                : t('home.chart.partial_none')}
            </p>
          ) : null}
          <details>
            <summary>{t('home.chart.table')}</summary>
            <table className="chart-table">
              <thead>
                <tr>
                  <th scope="col">{t('home.chart.day')}</th>
                  {chart.series.map((each) => (
                    <th scope="col" key={each.key || 'other'}>
                      {nameOf(each.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chart.days.map((day, index) => (
                  <tr key={day}>
                    <th scope="row">{formatDate(day)}</th>
                    {chart.series.map((each) => (
                      <td key={each.key || 'other'} className="num">
                        {each.counts[index] ?? t('home.chart.not_read')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      ) : null}
    </section>
  )
}
